"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { User, ChevronDown, LogOut, ShoppingBag, Package } from "lucide-react";
import { cn } from "@/lib/utils";

interface CustomerInfo {
  email: string;
  firstName: string | null;
  lastName: string | null;
}

export function AccountDropdown() {
  const [open, setOpen] = useState(false);
  const [customer, setCustomer] = useState<CustomerInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useEffect(() => {
    async function fetchCustomer() {
      try {
        const res = await fetch("/api/customer/auth/me");
        const data = await res.json();
        if (data.authenticated && data.customer) {
          setCustomer(data.customer);
        }
      } catch {
        // Not authenticated
      } finally {
        setLoading(false);
      }
    }
    void fetchCustomer();
  }, []);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  async function handleLogout() {
    setLoggingOut(true);
    await fetch("/api/customer/auth/logout", { method: "POST" });
    setCustomer(null);
    setOpen(false);
    router.push("/account/login");
    router.refresh();
  }

  const displayName = customer
    ? customer.firstName ?? customer.email.split("@")[0]
    : null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 p-2 text-[#9A9A9A] hover:text-[#F5F5F5] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
        aria-label="Account menu"
        aria-expanded={open}
        suppressHydrationWarning
      >
        <User size={20} />
        {!loading && displayName && (
          <span className="hidden md:block text-xs font-medium max-w-[100px] truncate">
            {displayName}
          </span>
        )}
        <ChevronDown
          size={14}
          className={cn("transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full pt-2 w-56"
          style={{ zIndex: 50 }}
        >
          <div
            className="bg-[#141414] border border-[#2A2A2A] rounded-md shadow-2xl py-1"
            onClick={() => setOpen(false)}
          >
            {customer ? (
              <>
                <div
                  className="px-4 py-3 border-b"
                  style={{ borderColor: "#2A2A2A" }}
                >
                  <p className="text-xs font-semibold truncate" style={{ color: "#8A8A8A" }}>
                    Signed in as
                  </p>
                  <p className="text-sm truncate" style={{ color: "#F5F5F5" }}>
                    {customer.email}
                  </p>
                </div>
                <Link
                  href="/account"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-[#1E1E1E] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                  style={{ color: "#F5F5F5" }}
                >
                  <Package size={16} style={{ color: "#9A9A9A" }} />
                  My Orders
                </Link>
                <Link
                  href="/account/builds"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-[#1E1E1E] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                  style={{ color: "#F5F5F5" }}
                >
                  <ShoppingBag size={16} style={{ color: "#9A9A9A" }} />
                  My Builds
                </Link>
                <div
                  className="border-t py-1"
                  style={{ borderColor: "#2A2A2A" }}
                >
                  <button
                    onClick={handleLogout}
                    disabled={loggingOut}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm w-full hover:bg-[#1E1E1E] transition-colors disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                    style={{ color: "#9A9A9A" }}
                  >
                    <LogOut size={16} />
                    {loggingOut ? "Signing out..." : "Sign Out"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="px-4 py-3 border-b" style={{ borderColor: "#2A2A2A" }}>
                  <p className="text-sm font-semibold" style={{ color: "#F5F5F5" }}>
                    My Account
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: "#8A8A8A" }}>
                    Sign in to track orders and saved builds
                  </p>
                </div>
                <Link
                  href="/account/login"
                  className="block px-4 py-2.5 text-sm hover:bg-[#1E1E1E] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                  style={{ color: "#E8C547" }}
                >
                  Sign In
                </Link>
                <Link
                  href="/account/register"
                  className="block px-4 py-2.5 text-sm hover:bg-[#1E1E1E] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                  style={{ color: "#F5F5F5" }}
                >
                  Create Account
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
